<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { usePromptLibraryStore } from '@/stores/prompt_library';
import { useAuthStore } from '@/stores/auth';
import { useRowDelete } from '@/composables/useRowDelete';
import type { PromptCreate, PromptCollectionInDb } from '@/api';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaAccent,
  gaiaContentWidth,
  gaiaFontSize,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';

/**
 * Prompt Collections — the Prompt Collections mock. Same list anatomy as the
 * other repository pages: serif title + count, one "New ▾" action carrying the
 * three creation paths, search, and a data card whose rows carry their own
 * confirm/busy/error strips. Search and sort stay server-side; the library
 * import and AI generation flows are unchanged.
 */

const router = useRouter();
const message = useGaiaToast();
const store = usePromptCollectionsStore();
const libraryStore = usePromptLibraryStore();
const authStore = useAuthStore();
const { collections, totalCount, isLoading, error } = storeToRefs(store);
const { metaCategories, isLoading: isLibraryLoading } = storeToRefs(libraryStore);

const rowDelete = useRowDelete<PromptCollectionInDb>();

// Create / rename share one dialog: the same single field either way.
const nameDialogOpen = ref<boolean>(false);
const editingCollection = ref<PromptCollectionInDb | null>(null);
const nameDraft = ref<string>('');
const savingName = ref<boolean>(false);

// For import functionality
const showImportModal = ref<boolean>(false);
const isImporting = ref<boolean>(false);
const importProgress = ref<number>(0);
const importTotal = ref<number>(0);
const parsedPrompts = ref<PromptCreate[]>([]);
const selectedLibraryKeys = ref<string[]>([]);
const isLibraryDropdownOpen = ref(false);

// For search functionality
const searchTerm = ref<string>('');
const currentPage = ref<number>(1);
const pageSize = ref<number>(20);

// Server-side sort state — sent to the search endpoint as sort_key/sort_direction
type SortKey = 'name' | 'updated_at' | 'created_at';
const sortState = ref<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);

// For AI generation modal
const showAIGenerateModal = ref<boolean>(false);
const aiCollectionName = ref<string>('');
const aiInstructions = ref<string>('');
const aiContext = ref<string>('');
const isGeneratingWithAI = ref<boolean>(false);

const isOrgAdmin = computed(() => authStore.userPermissions.is_org_admin);

// All create actions live under the "+ New" dropdown, like the file vault's.
const newActionOptions = computed(() => [
  { label: 'New collection', key: 'create-collection', disabled: !isOrgAdmin.value },
  { label: 'Import from library', key: 'import-library', disabled: !isOrgAdmin.value },
  { label: 'Generate with AI', key: 'generate-ai', disabled: !isOrgAdmin.value },
]);

// --- derived -----------------------------------------------------------------
const hasQuery = computed(() => searchTerm.value.trim().length > 0);

const countLabel = computed(() => {
  if (isLoading.value) return 'Loading';
  return `${totalCount.value} ${totalCount.value === 1 ? 'collection' : 'collections'}`;
});

const showEmpty = computed(
  () => !isLoading.value && collections.value.length === 0 && !hasQuery.value
);
const showNoMatch = computed(
  () => !isLoading.value && collections.value.length === 0 && hasQuery.value
);
const showRows = computed(() => !isLoading.value && collections.value.length > 0);

const skeletonCells = ['58%', '120px', '120px'] as const;

const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Name', width: 'minmax(280px,2fr)', sortable: true },
  { key: 'updated_at', label: 'Last modified', width: '160px', sortable: true },
  { key: 'created_at', label: 'Created', width: '160px', sortable: true },
];

const emptyCell = '—';

function formatDate(raw: string | null | undefined): string {
  if (!raw) return emptyCell;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return emptyCell;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function promptCountLabel(row: PromptCollectionInDb): string {
  const count = row.prompt_count ?? 0;
  return `${count}`;
}

// --- data --------------------------------------------------------------------
onMounted(async (): Promise<void> => {
  await Promise.all([fetchCollections(), libraryStore.initialize()]);
});

async function fetchCollections(): Promise<void> {
  await store.fetchCollections({
    search_term: searchTerm.value,
    page: currentPage.value,
    page_size: pageSize.value,
    sort_key: sortState.value?.key,
    sort_direction: sortState.value?.direction,
  });

  if (error.value) {
    message.error(error.value);
  }
}

async function applySearch(): Promise<void> {
  currentPage.value = 1;
  await fetchCollections();
}

function clearSearch(): void {
  searchTerm.value = '';
  currentPage.value = 1;
  void fetchCollections();
}

function onSort(key: string): void {
  const sortKey = key as SortKey;
  if (sortState.value?.key === sortKey) {
    sortState.value = {
      key: sortKey,
      direction: sortState.value.direction === 'asc' ? 'desc' : 'asc',
    };
  } else {
    // Dates open newest-first; a name opens A–Z.
    sortState.value = { key: sortKey, direction: sortKey === 'name' ? 'asc' : 'desc' };
  }
  currentPage.value = 1;
  void fetchCollections();
}

watch([currentPage, pageSize], () => {
  void fetchCollections();
});

// --- navigation ----------------------------------------------------------------
function navigateToCollection(id: string): void {
  void router.push(`/prompt-collections/${id}`);
}

function handleHeaderActionSelect(key: string | number): void {
  if (!isOrgAdmin.value) return;
  switch (String(key)) {
    case 'import-library':
      showImportModal.value = true;
      return;
    case 'generate-ai':
      showAIGenerateModal.value = true;
      return;
    case 'create-collection':
      openCreate();
      return;
  }
}

// --- create / rename -------------------------------------------------------------
function openCreate(): void {
  editingCollection.value = null;
  nameDraft.value = '';
  nameDialogOpen.value = true;
}

function openRename(collection: PromptCollectionInDb): void {
  editingCollection.value = collection;
  nameDraft.value = collection.name;
  nameDialogOpen.value = true;
}

async function submitNameDialog(): Promise<void> {
  const name = nameDraft.value.trim();
  if (!name) return;

  savingName.value = true;
  try {
    if (editingCollection.value) {
      if (name === editingCollection.value.name) {
        nameDialogOpen.value = false;
        return;
      }
      const success = await store.updateCollection(editingCollection.value.uid, { name });
      if (success) {
        message.success('Collection renamed');
        nameDialogOpen.value = false;
        await fetchCollections();
      } else if (error.value) {
        message.error(`Failed to rename collection: ${error.value}`);
      }
    } else {
      const result = await store.createCollection(name);
      if (result) {
        message.success('Collection created');
        nameDialogOpen.value = false;
        await fetchCollections();
      } else if (error.value) {
        message.error(`Failed to create collection: ${error.value}`);
      }
    }
  } finally {
    savingName.value = false;
  }
}

// --- delete ----------------------------------------------------------------------
async function deleteCollection(collection: PromptCollectionInDb): Promise<void> {
  await rowDelete.run(collection, {
    work: async () => {
      const success = await store.deleteCollection(collection.uid);
      if (!success) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        throw new Error(error.value || 'Delete failed');
      }
      message.success('Collection deleted');
      await fetchCollections();
    },
    errorText: () => 'Couldn’t delete this collection. Nothing was changed.',
  });
}

// --- duplicate -------------------------------------------------------------------
async function handleDuplicateCollection(collection: PromptCollectionInDb): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to create prompts.');
    return;
  }
  try {
    const newCollectionName = `COPY - ${collection.name}`;
    const newCollection = await store.createCollection(newCollectionName);

    if (!newCollection) {
      message.error('Failed to create duplicate collection');
      return;
    }

    const originalPrompts = await store.getCollectionPrompts(collection.uid);

    if (originalPrompts.length === 0) {
      message.success(
        `Collection "${newCollectionName}" created successfully (no prompts to copy)`
      );
      await fetchCollections();
      return;
    }

    let successCount = 0;
    for (const prompt of originalPrompts) {
      try {
        await store.addPrompt(newCollection.uid, {
          name: prompt.name,
          prompt: prompt.prompt,
          response_type: prompt.response_type,
          evaluation_prompt: prompt.evaluation_prompt,
          evaluation_response_type: prompt.evaluation_response_type,
          suggestion_prompt: prompt.suggestion_prompt,
          suggestion_example_data: prompt.suggestion_example_data,
        });
        successCount++;
      } catch (error_) {
        logger.error(`Error copying prompt "${prompt.name}":`, error_);
      }
    }

    message.success(
      `Collection "${newCollectionName}" created successfully with ${successCount} of ${originalPrompts.length} prompts copied`
    );
    await fetchCollections();
  } catch (error_) {
    logger.error('Error duplicating collection:', error_);
    message.error('Failed to duplicate collection');
  }
}

// --- library import ---------------------------------------------------------------
// Tree-select data for Library Import — category → collections
const libraryTreeSelectData = computed(() => {
  return metaCategories.value.map(cat => ({
    key: `cat:${cat.name}`,
    label: cat.name,
    children: cat.categories.map(col => {
      const promptCountSuffix = col.prompts.length ? ` (${col.prompts.length} prompts)` : '';
      return {
        key: `${cat.name}|||${col.name}`,
        label: `${col.name}${promptCountSuffix}`,
      };
    }),
  }));
});

const selectedLibraryCount = computed(
  () => selectedLibraryKeys.value.filter(k => k.includes('|||')).length
);

const librarySelectionLabel = computed(() => {
  if (selectedLibraryCount.value === 0) return 'Nothing selected';
  return `${selectedLibraryCount.value} ${
    selectedLibraryCount.value === 1 ? 'collection' : 'collections'
  } · ${parsedPrompts.value.length} prompts`;
});

// Populate parsedPrompts from the tree-selected library collections
function selectLibraryPromptsFromKeys(): void {
  parsedPrompts.value = [];

  const leafKeys = selectedLibraryKeys.value.filter(k => k.includes('|||'));
  if (leafKeys.length === 0) return;

  for (const key of leafKeys) {
    const sepIdx = key.indexOf('|||');
    const catName = key.substring(0, sepIdx);
    const colName = key.substring(sepIdx + 3);

    const category = metaCategories.value.find(c => c.name === catName);
    if (!category) continue;
    const collection = category.categories.find(c => c.name === colName);
    if (!collection) continue;

    for (const prompt of collection.prompts) {
      if (!prompt.name || !prompt.prompt) continue;
      parsedPrompts.value.push({
        name: prompt.name,
        prompt: prompt.prompt,
        response_type: prompt.response_type,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        evaluation_prompt: prompt.evaluation_prompt || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        evaluation_response_type: prompt.evaluation_response_type || null,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
        suggestion_prompt: prompt.suggestion_prompt || null,
        suggestion_example_data: prompt.suggestion_example_data ?? null,
        suggestion_tool_selection: prompt.suggestion_tool_selection ?? null,
      });
    }
  }

  if (parsedPrompts.value.length === 0) {
    message.warning('No valid prompts found in the selected collections');
  }
}

// Imports a single collection (create + add all its prompts). Returns true on success.
async function importSingleCollection(key: string): Promise<boolean> {
  const sepIdx = key.indexOf('|||');
  const catName = key.substring(0, sepIdx);
  const colName = key.substring(sepIdx + 3);

  const category = metaCategories.value.find(c => c.name === catName);
  const collection = category?.categories.find(c => c.name === colName);
  if (!collection?.prompts.length) return false;

  const newCollection = await store.createCollection(colName);
  if (!newCollection) {
    logger.error(`Failed to create collection "${colName}"`);
    return false;
  }

  for (const prompt of collection.prompts) {
    if (!prompt.name || !prompt.prompt) continue;
    const result = await store.addPrompt(newCollection.uid, {
      name: prompt.name,
      prompt: prompt.prompt,
      response_type: prompt.response_type,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      evaluation_prompt: prompt.evaluation_prompt || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      evaluation_response_type: prompt.evaluation_response_type || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      suggestion_prompt: prompt.suggestion_prompt || null,
      suggestion_example_data: prompt.suggestion_example_data ?? null,
      suggestion_tool_selection: prompt.suggestion_tool_selection ?? null,
    });
    if (!result) {
      logger.error(`Failed to add prompt "${prompt.name}" to "${colName}"`);
    }
  }
  return true;
}

// Create collection with imported prompts — all collections start in parallel,
// modal stays open until every one finishes.
async function createCollectionWithPrompts(): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to create prompts.');
    return;
  }

  const leafKeys = selectedLibraryKeys.value.filter(k => k.includes('|||'));
  if (leafKeys.length === 0) {
    message.error('No collections selected');
    return;
  }

  isImporting.value = true;
  importProgress.value = 0;
  importTotal.value = leafKeys.length;

  try {
    const results = await Promise.allSettled(
      leafKeys.map(key =>
        importSingleCollection(key).then(result => {
          importProgress.value++;
          return result;
        })
      )
    );

    const createdCount = results.filter(r => r.status === 'fulfilled' && r.value).length;

    message.success(`Created ${createdCount} of ${leafKeys.length} collection(s) from library`);

    showImportModal.value = false;
    parsedPrompts.value = [];
    selectedLibraryKeys.value = [];

    await fetchCollections();
  } catch (error_) {
    logger.error('Error creating collections with prompts:', error_);
    message.error('Failed to create collections with prompts');
  } finally {
    isImporting.value = false;
  }
}

// Auto-populate parsedPrompts when tree-select changes
watch(selectedLibraryKeys, () => {
  selectLibraryPromptsFromKeys();
});

// --- AI generation ------------------------------------------------------------------
async function handleGenerateCollectionWithAI(): Promise<void> {
  if (!isOrgAdmin.value) {
    message.error('You do not have permission to create prompts.');
    return;
  }
  if (!aiCollectionName.value.trim() || !aiInstructions.value.trim()) return;

  isGeneratingWithAI.value = true;

  try {
    const result = await store.createCollectionWithAI({
      name: aiCollectionName.value.trim(),
      instructions: aiInstructions.value.trim(),
      context: aiContext.value.trim(),
    });

    if (result) {
      message.success('Collection generated successfully with AI');
      showAIGenerateModal.value = false;
      aiCollectionName.value = '';
      aiInstructions.value = '';
      aiContext.value = '';
      await fetchCollections();
    } else if (error.value) {
      message.error(`Failed to generate collection: ${error.value}`);
    }
  } catch (error_) {
    logger.error('Error generating collection with AI:', error_);
    message.error('Failed to generate collection with AI');
  } finally {
    isGeneratingWithAI.value = false;
  }
}

// --- chrome --------------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceSoft;
const tileSurface = gaiaNeutral.surfaceMuted;
const tileBorder = gaiaNeutral.borderMuted;
const tileColor = gaiaNeutral.textTertiary;
const pillSurface = gaiaAccent.tint;
const pillColor = gaiaAccent.base;
const pillSize = gaiaFontSize.nano;
const pillWeight = String(gaiaFontWeight.semibold);
const alertColor = gaiaStatus.alert;
const alertHover = gaiaStatus.alertHover;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.promptLibrary }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18">
      <!-- Title + count, toolbar pushed right. -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex vertical :size="6" :style="{ flex: '1 1 320px', minWidth: 0 }">
          <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
            <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
              Prompt Collections
            </gaia-header>
            <gaia-count-label>{{ countLabel }}</gaia-count-label>
          </n-flex>
          <gaia-text size="row" tone="tertiary" :style="{ maxWidth: '62ch' }">
            Reusable sets of extraction prompts. Point a review, a playbook or a chat at a
            collection instead of rewriting the questions.
          </gaia-text>
        </n-flex>

        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchTerm"
            placeholder="Search collections"
            :debounce="500"
            @search="applySearch"
          />

          <gaia-dropdown
            trigger="click"
            :options="newActionOptions"
            placement="bottom-end"
            @select="handleHeaderActionSelect"
          >
            <gaia-button type="primary" size="medium">
              <template #icon>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </template>
              New
              <svg
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                :style="{ marginLeft: '4px', opacity: 0.7 }"
              >
                <path d="M4.5 6.5 8 10l3.5-3.5" />
              </svg>
            </gaia-button>
          </gaia-dropdown>
        </n-flex>
      </n-flex>

      <gaia-data-table
        :columns="tableColumns"
        :sort-key="sortState?.key ?? ''"
        :sort-direction="sortState?.direction ?? 'desc'"
        :sort-active="sortState !== null"
        :action-width="104"
        @sort="onSort"
      >
        <template #default="{ rowStyle }">
          <!-- Loading: the toolbar and header stay live, the body shimmers. -->
          <gaia-table-skeleton
            v-if="isLoading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
          />

          <gaia-empty-state v-else-if="showEmpty" title="No prompt collections yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M4 2.5h6.5L13 5v8.5H4z" />
                <path d="M6 6.5h4M6 9h2.5" />
              </svg>
            </template>
            A collection groups the questions you always ask of one contract type — build one from
            scratch or import a set you already use.
            <template v-if="isOrgAdmin" #actions>
              <gaia-button size="small" @click="showImportModal = true">
                Import from library
              </gaia-button>
              <gaia-button type="primary" size="small" @click="openCreate">
                New collection
              </gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state
            v-else-if="showNoMatch"
            :title="`No collections match “${searchTerm.trim()}”`"
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            Search covers collection names. Clear it to see every collection.
            <template #actions>
              <gaia-button size="small" @click="clearSearch">Clear search</gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else>
            <template v-for="collection in collections" :key="collection.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(collection.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${collection.name}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteCollection(collection)"
              >
                The collection and its prompts are removed for everyone. Reviews already run with it
                keep their results.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(collection.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ collection.name }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(collection.uid) === 'error'"
                variant="error"
                @retry="deleteCollection(collection)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(collection.uid) }}
              </gaia-table-row-state>

              <div
                v-else
                class="row"
                :style="rowStyle"
                @click="navigateToCollection(collection.uid)"
              >
                <div class="cell name-cell">
                  <span class="icon-tile">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M4 2.5h6.5L13 5v8.5H4z" />
                      <path d="M6 6.5h4M6 9h2.5" />
                    </svg>
                  </span>
                  <gaia-text truncate size="row" weight="semibold" tone="ink">
                    <router-link
                      :to="`/prompt-collections/${collection.uid}`"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ collection.name }}
                    </router-link>
                  </gaia-text>
                  <span class="count-pill" title="Prompts in this collection">
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M4.5 4.5h6.5l0 9h-6.5z" />
                      <path d="M6.5 2.5h6.5v9" />
                    </svg>
                    {{ promptCountLabel(collection) }}
                  </span>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="text"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDate(collection.updated_at ?? collection.created_at) }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDate(collection.created_at) }}
                  </gaia-text>
                </div>

                <n-flex v-if="isOrgAdmin" justify="end" :size="2" :wrap="false">
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    title="Rename collection"
                    @click.stop="openRename(collection)"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M2.5 12.5 12 3l1 1-9.5 9.5-1.5.5z" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    title="Duplicate collection"
                    @click.stop="handleDuplicateCollection(collection)"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                      <path d="M10.5 3.5H4a1.5 1.5 0 0 0-1.5 1.5v6" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    title="Delete collection"
                    class="delete-action"
                    @click.stop="rowDelete.arm(collection)"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M3.5 5h9M6.5 5V3.5h3V5M5 5l.5 8h5L11 5" />
                    </svg>
                  </gaia-icon-button>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <!-- Create and rename share one dialog: the same single field either way. -->
    <gaia-modal
      :show="nameDialogOpen"
      width="460px"
      @update:show="(value: boolean) => (nameDialogOpen = value)"
    >
      <template #header>
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          {{ editingCollection ? 'Rename collection' : 'Create new collection' }}
        </gaia-text>
      </template>

      <gaia-form-field label="Name" required>
        <gaia-input
          v-model:value="nameDraft"
          placeholder="Enter collection name"
          style="width: 100%"
          @keyup.enter="submitNameDialog"
        />
      </gaia-form-field>

      <template #footer>
        <n-flex justify="end" :size="8">
          <gaia-button size="small" @click="nameDialogOpen = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :loading="savingName"
            :disabled="!nameDraft.trim()"
            @click="submitNameDialog"
          >
            {{ editingCollection ? 'Rename' : 'Create' }}
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Import from library -->
    <gaia-modal
      :show="showImportModal"
      width="560px"
      @update:show="(value: boolean) => !isImporting && (showImportModal = value)"
    >
      <template #header>
        <n-flex vertical :size="2">
          <gaia-text size="subtitle" weight="semibold" tone="ink">Import from library</gaia-text>
          <gaia-text size="meta" tone="subtle">
            Pick whole categories or single collections from the Gaia prompt library.
          </gaia-text>
        </n-flex>
      </template>

      <n-flex vertical :size="14">
        <n-spin :show="isLibraryLoading">
          <n-tree-select
            v-model:value="selectedLibraryKeys"
            :options="libraryTreeSelectData"
            multiple
            cascade
            checkable
            check-strategy="child"
            filterable
            clearable
            :max-height="240"
            placeholder="Select categories or collections…"
            style="width: 100%"
            @update:show="isLibraryDropdownOpen = $event"
          />
        </n-spin>

        <!-- Spacer that expands while the dropdown is open, so the tree isn't
             clipped by the modal card. -->
        <div
          class="library-dropdown-spacer"
          :class="{ 'library-dropdown-spacer--open': isLibraryDropdownOpen }"
        />

        <n-flex v-if="isImporting" vertical :size="6">
          <gaia-progress-bar :percentage="Math.round((importProgress / importTotal) * 100)" />
          <gaia-text size="micro" tone="subtle">
            {{ importProgress }} / {{ importTotal }} imported
          </gaia-text>
        </n-flex>
      </n-flex>

      <template #footer>
        <n-flex align="center" :size="10">
          <gaia-text size="micro" tone="subtle">{{ librarySelectionLabel }}</gaia-text>
          <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
            <gaia-button size="small" :disabled="isImporting" @click="showImportModal = false">
              Cancel
            </gaia-button>
            <gaia-button
              type="primary"
              size="small"
              :loading="isImporting"
              :disabled="!isOrgAdmin || selectedLibraryCount === 0"
              @click="createCollectionWithPrompts"
            >
              Import
            </gaia-button>
          </n-flex>
        </n-flex>
      </template>
    </gaia-modal>

    <!-- Generate with AI -->
    <gaia-modal
      :show="showAIGenerateModal"
      width="620px"
      @update:show="(value: boolean) => (showAIGenerateModal = value)"
    >
      <template #header>
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          Generate collection with AI
        </gaia-text>
      </template>

      <n-flex vertical :size="16">
        <gaia-form-field label="Name" required>
          <gaia-input
            v-model:value="aiCollectionName"
            placeholder="Enter collection name"
            style="width: 100%"
          />
        </gaia-form-field>

        <gaia-form-field label="Instructions" required>
          <gaia-textarea
            v-model:value="aiInstructions"
            placeholder="e.g. Create prompts for analysing investment agreements"
            :autosize="{ minRows: 3, maxRows: 6 }"
            style="width: 100%"
          />
        </gaia-form-field>

        <gaia-form-field
          label="Context"
          hint="Paste anything that helps the model understand the documents you want to screen — a guarantee section, a sample contract, or your own checklist."
        >
          <gaia-textarea
            v-model:value="aiContext"
            placeholder="Paste document content here…"
            :autosize="{ minRows: 6, maxRows: 14 }"
            style="width: 100%"
          />
        </gaia-form-field>
      </n-flex>

      <template #footer>
        <n-flex justify="end" :size="8">
          <gaia-button size="small" @click="showAIGenerateModal = false">Cancel</gaia-button>
          <gaia-button
            type="primary"
            size="small"
            :loading="isGeneratingWithAI"
            :disabled="!isOrgAdmin || !aiCollectionName.trim() || !aiInstructions.trim()"
            @click="handleGenerateCollectionWithAI"
          >
            <template #icon>
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M7 2.4 8.1 5.3 11 6.4 8.1 7.5 7 10.4 5.9 7.5 3 6.4l2.9-1.1z" />
              </svg>
            </template>
            Generate
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 18px;
  min-height: 54px;
  padding: 6px 20px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.name-cell {
  gap: 10px;
}

/* The collection's mark — a document sheaf on a muted tile. */
.icon-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid v-bind(tileBorder);
  background: v-bind(tileSurface);
  color: v-bind(tileColor);
}

/* Prompt count as the mock's violet pill — an accent mark, not a status. */
.count-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
  height: 19px;
  padding: 0 8px;
  border-radius: 9px;
  background: v-bind(pillSurface);
  color: v-bind(pillColor);
  font-size: v-bind(pillSize);
  font-weight: v-bind(pillWeight);
  font-variant-numeric: tabular-nums;
}

.delete-action {
  color: v-bind(alertColor);
}

.delete-action:hover {
  background: v-bind(alertHover);
}

.library-dropdown-spacer {
  height: 0;
  overflow: hidden;
  transition: height 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.library-dropdown-spacer--open {
  height: 260px;
}
</style>
